import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getShops } from "../utils/api";

export default function Home() {
  const [shops, setShops] = useState([]);
  const [search, setSearch] = useState("");
  const [city, setCity] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchShops = async () => {
    setLoading(true);
    try {
      const res = await getShops({ search, city });
      setShops(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchShops(); }, []);

  const popularCities = ["Brahmapur", "Bhubaneswar", "Cuttack", "Puri", "Rourkela"];

  return (
    <div style={styles.page}>

      {/* ── Hero ── */}
      <div style={styles.hero}>
        <div style={styles.heroBg} />
        <div style={styles.heroContent}>
          <div style={styles.heroBadge}>✂️ Odisha's #1 Beauty Booking App</div>
          <h1 style={styles.heroTitle}>
            Find & Book Your<br />
            <span style={styles.heroAccent}>Perfect Salon</span>
          </h1>
          <p style={styles.heroSub}>
            Discover top-rated Salon shops near you. Book instantly, no waiting!
          </p>

          {/* Search Card */}
          <div style={styles.searchCard}>
            <div style={styles.inputGroup}>
              <span style={styles.inputIcon}>🔍</span>
              <input
                placeholder="Search Salon shops..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchShops()}
                style={styles.searchInput}
              />
            </div>
            <div style={styles.inputGroup}>
              <span style={styles.inputIcon}>📍</span>
              <input
                placeholder="Enter your city..."
                value={city}
                onChange={(e) => setCity(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchShops()}
                style={styles.searchInput}
              />
            </div>
            <button onClick={fetchShops} style={styles.searchBtn}>
              Search →
            </button>

            {/* Popular Cities */}
            <div style={styles.popularRow}>
              <span style={styles.popularLabel}>Popular:</span>
              {popularCities.map((c) => (
                <button
                  key={c}
                  onClick={() => { setCity(c); setTimeout(fetchShops, 100); }}
                  style={styles.cityChip}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div style={styles.heroStats}>
            {[
              { num: "500+", label: "Happy Customers" },
              { num: "50+", label: "Salon Shops" },
              { num: "4.8⭐", label: "Average Rating" },
            ].map((stat, i) => (
              <div key={i} style={styles.heroStat}>
                <span style={styles.heroStatNum}>{stat.num}</span>
                <span style={styles.heroStatLabel}>{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Shops Section ── */}
      <div style={styles.container}>
        <div style={styles.sectionHeader}>
          <div>
            <h2 style={styles.sectionTitle}>
              {loading ? "Finding shops..." : city ? `Shops in ${city}` : "All Shops"}
            </h2>
            <p style={styles.sectionSubtitle}>
              {!loading && `${shops.length} shops found`}
            </p>
          </div>
        </div>

        {loading && (
          <div style={styles.grid}>
            {[1,2,3,4,5,6].map((i) => (
              <div key={i} style={styles.skeletonCard}>
                <div style={styles.skeletonImg} />
                <div style={{ padding: "16px" }}>
                  <div style={styles.skeletonLine} />
                  <div style={{ ...styles.skeletonLine, width: "60%" }} />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && shops.length > 0 && (
          <div style={styles.grid}>
            {shops.map((shop, index) => (
              <ShopCard key={shop.id} shop={shop} index={index} />
            ))}
          </div>
        )}

        {!loading && shops.length === 0 && (
  <div style={styles.emptyState}>
    <p style={{ fontSize: "56px", marginBottom: "12px" }}>
      {city ? "🏙️" : "✂️"}
    </p>
    <h3 style={{ color: "white", marginBottom: "8px", fontSize: "1.2rem" }}>
      {city ? `No salons in ${city} yet` : "No shops found"}
    </h3>
    <p style={{ color: "var(--text-muted)", marginBottom: "8px", fontSize: "14px", lineHeight: 1.6 }}>
      {city
        ? `TrimSpot is growing! We haven't reached ${city} yet — but we're expanding across Odisha soon.`
        : "Try searching with a different name or city."}
    </p>
    {city && (
      <p style={{ color: "#4caf50", fontSize: "13px", marginBottom: "20px" }}>
        🏪 Are you a salon owner in {city}? List your shop for free!
      </p>
    )}
    <div style={{ display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap" }}>
      <button onClick={() => { setSearch(""); setCity(""); fetchShops(); }} style={styles.resetBtn}>
        Browse All Shops
      </button>
    </div>
  </div>
)}
      </div>

      {/* ── Customer CTA ── */}
      <div style={styles.cta}>
        <div style={styles.ctaInner}>
          <div>
            <h3 style={styles.ctaTitle}>📅 Never Wait Again!</h3>
            <p style={styles.ctaText}>Book your next appointment in under 3 minutes. No calls, no waiting!</p>
          </div>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            style={styles.ctaBtn}
          >
            Book Now →
          </button>
        </div>
      </div>

    </div>
  );
}

function ShopCard({ shop, index }) {
  const [hovered, setHovered] = useState(false);

  const isOpen = () => {
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    if (!shop.opening_time || !shop.closing_time) return true;
    const [openH, openM] = shop.opening_time.split(":").map(Number);
    const [closeH, closeM] = shop.closing_time.split(":").map(Number);
    return currentTime >= (openH * 60 + openM) && currentTime <= (closeH * 60 + closeM);
  };

  const open = isOpen();

  const gradients = [
    "linear-gradient(135deg, #1a1a2e, #e94560)",
    "linear-gradient(135deg, #0f3460, #e94560)",
    "linear-gradient(135deg, #16213e, #533483)",
    "linear-gradient(135deg, #1a1a2e, #ff6b35)",
    "linear-gradient(135deg, #0d0d0d, #00b4d8)",
    "linear-gradient(135deg, #1a1a2e, #06d6a0)",
  ];
  const gradient = gradients[index % gradients.length];

  return (
    <div
      style={{
        ...styles.card,
        transform: hovered ? "translateY(-6px)" : "translateY(0)",
        boxShadow: hovered ? "0 20px 40px rgba(233,69,96,0.2)" : "0 2px 12px rgba(0,0,0,0.3)",
        border: hovered ? "1px solid var(--accent)" : "1px solid var(--border)",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={styles.cardImgWrapper}>
        {shop.photo ? (
          <img src={shop.photo} alt={shop.name} style={styles.cardRealImg}
            onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }} />
        ) : null}
        <div style={{ ...styles.cardImgPlaceholder, background: gradient, display: shop.photo ? "none" : "flex" }}>
          <span style={{ fontSize: "40px" }}>✂️</span>
          <span style={styles.placeholderShopName}>{shop.name?.charAt(0).toUpperCase()}</span>
        </div>
        <div style={{ ...styles.openBadge, background: open ? "#4caf50" : "#e94560" }}>
          {open ? "● Open" : "● Closed"}
        </div>
        {!shop.is_subscribed && (
          <div style={styles.notAcceptingBadge}>Not Booking</div>
        )}
        {shop.home_service_enabled && (
          <div style={styles.homeServiceBadge}>🏠 Home Service</div>
        )}
      </div>

      <div style={styles.cardBody}>
        <h3 style={styles.cardTitle}>{shop.name}</h3>
        <div style={styles.cardMeta}>
          <span style={styles.cardCity}>📍 {shop.city}</span>
          {shop.opening_time && (
            <span style={styles.cardHours}>🕐 {formatTime(shop.opening_time)}-{formatTime(shop.closing_time)}</span>
          )}
        </div>
        <p style={styles.cardAddress}>{shop.address}</p>
        <div style={styles.ratingRow}>
          <div style={styles.stars}>
            {[1,2,3,4,5].map((star) => (
              <span key={star} style={{ color: star <= Math.round(shop.avg_rating || 0) ? "#f5a623" : "#333", fontSize: "14px" }}>★</span>
            ))}
          </div>
          <span style={styles.ratingNum}>
            {shop.avg_rating > 0 ? Number(shop.avg_rating).toFixed(1) : "New"}
          </span>
          {shop.total_reviews > 0 && (
            <span style={styles.reviewCount}>({shop.total_reviews})</span>
          )}
        </div>
        <div style={styles.cardFooter}>
          <span style={styles.priceFrom}>💈 Book Appointment</span>
          <Link to={`/shops/${shop.id}`}
            style={{ ...styles.bookBtn, background: shop.is_subscribed ? "var(--accent)" : "#333" }}>
            {shop.is_subscribed ? "Book Now →" : "View →"}
          </Link>
        </div>
      </div>
    </div>
  );
}

function formatTime(timeStr) {
  if (!timeStr) return "";
  const [hourStr, minuteStr] = timeStr.split(":");
  let hour = parseInt(hourStr);
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${hour}:${minuteStr} ${ampm}`;
}

const styles = {
  page: { minHeight: "100vh", background: "var(--bg-primary)" },
  hero: { background: "linear-gradient(135deg, #0a0a0f 0%, #13131a 50%, #1a0a1e 100%)", padding: "50px 16px 60px", position: "relative", overflow: "hidden", borderBottom: "1px solid var(--border)" },
  heroBg: { position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "radial-gradient(circle at 70% 30%, rgba(233,69,96,0.12) 0%, transparent 60%)", pointerEvents: "none" },
  heroContent: { maxWidth: "700px", margin: "0 auto", position: "relative", zIndex: 1, textAlign: "center", width: "100%" },
  heroBadge: { display: "inline-block", background: "rgba(233,69,96,0.15)", color: "var(--accent)", padding: "6px 16px", borderRadius: "20px", fontSize: "13px", fontWeight: "600", marginBottom: "16px", border: "1px solid rgba(233,69,96,0.3)" },
  heroTitle: { fontSize: "clamp(1.8rem, 5vw, 3rem)", fontWeight: "900", color: "white", lineHeight: 1.2, marginBottom: "12px" },
  heroAccent: { color: "var(--accent)" },
  heroSub: { color: "var(--text-secondary)", fontSize: "1rem", marginBottom: "30px" },
  searchCard: { background: "var(--bg-card)", borderRadius: "16px", padding: "16px", maxWidth: "600px", width: "100%", margin: "0 auto 30px", boxShadow: "0 10px 40px rgba(0,0,0,0.4)", border: "1px solid var(--border)", display: "flex", flexDirection: "column", gap: "10px", boxSizing: "border-box" },
  inputGroup: { display: "flex", alignItems: "center", background: "var(--bg-secondary)", borderRadius: "10px", padding: "0 12px", border: "1px solid var(--border)", width: "100%", boxSizing: "border-box", overflow: "hidden" },
  inputIcon: { fontSize: "16px", marginRight: "8px" },
  searchInput: { flex: 1, minWidth: 0, padding: "13px 0", border: "none", background: "transparent", fontSize: "15px", outline: "none", color: "var(--text-primary)", width: "100%" },
  searchBtn: { background: "var(--accent)", color: "white", border: "none", padding: "13px", borderRadius: "10px", cursor: "pointer", fontSize: "15px", fontWeight: "bold", width: "100%" },
  popularRow: { display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" },
  popularLabel: { color: "var(--text-muted)", fontSize: "13px" },
  cityChip: { background: "var(--bg-secondary)", border: "1px solid var(--border)", padding: "5px 12px", borderRadius: "20px", cursor: "pointer", fontSize: "12px", color: "var(--text-secondary)" },
  heroStats: { display: "flex", gap: "24px", justifyContent: "center", alignItems: "center", flexWrap: "wrap" },
  heroStat: { display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" },
  heroStatNum: { color: "white", fontWeight: "900", fontSize: "1.4rem" },
  heroStatLabel: { color: "var(--text-muted)", fontSize: "12px" },
  container: { maxWidth: "1100px", margin: "0 auto", padding: "30px 20px" },
  sectionHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "24px" },
  sectionTitle: { fontSize: "1.4rem", fontWeight: "800", color: "var(--text-primary)", margin: "0 0 4px" },
  sectionSubtitle: { color: "var(--text-muted)", fontSize: "13px", margin: 0 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: "20px" },
  card: { background: "var(--bg-card)", borderRadius: "16px", overflow: "hidden", transition: "all 0.3s ease", cursor: "pointer", border: "1px solid var(--border)" },
  cardImgWrapper: { position: "relative", height: "180px", overflow: "hidden" },
  cardRealImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  cardImgPlaceholder: { width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "6px", position: "absolute", top: 0, left: 0 },
  placeholderShopName: { color: "white", fontWeight: "900", fontSize: "24px", opacity: 0.5 },
  openBadge: { position: "absolute", top: "10px", left: "10px", color: "white", padding: "3px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: "bold" },
  notAcceptingBadge: { position: "absolute", top: "10px", right: "10px", background: "rgba(0,0,0,0.7)", color: "#aaa", padding: "3px 10px", borderRadius: "20px", fontSize: "10px" },
  cardBody: { padding: "16px" },
  cardTitle: { fontSize: "1rem", fontWeight: "700", color: "var(--text-primary)", margin: "0 0 8px" },
  cardMeta: { display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "4px" },
  cardCity: { color: "var(--accent)", fontSize: "12px", fontWeight: "500" },
  cardHours: { color: "var(--text-muted)", fontSize: "11px" },
  cardAddress: { color: "var(--text-muted)", fontSize: "12px", marginBottom: "10px" },
  ratingRow: { display: "flex", alignItems: "center", gap: "6px", marginBottom: "14px" },
  stars: { display: "flex" },
  ratingNum: { fontWeight: "700", fontSize: "13px", color: "var(--text-primary)" },
  reviewCount: { color: "var(--text-muted)", fontSize: "11px" },
  cardFooter: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  priceFrom: { color: "var(--text-muted)", fontSize: "12px" },
  bookBtn: { color: "white", padding: "8px 16px", borderRadius: "8px", textDecoration: "none", fontSize: "12px", fontWeight: "bold" },
  skeletonCard: { background: "var(--bg-card)", borderRadius: "16px", overflow: "hidden", border: "1px solid var(--border)" },
  skeletonImg: { height: "180px", background: "linear-gradient(90deg, var(--bg-card) 25%, var(--bg-hover) 50%, var(--bg-card) 75%)", backgroundSize: "200% 100%" },
  skeletonLine: { height: "12px", background: "var(--bg-hover)", borderRadius: "4px", marginBottom: "10px", width: "100%" },
  emptyState: { textAlign: "center", padding: "60px 20px" },
  resetBtn: { background: "var(--accent)", color: "white", border: "none", padding: "12px 24px", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  cta: { background: "var(--bg-secondary)", padding: "28px 20px", borderTop: "1px solid var(--border)" },
  ctaInner: { maxWidth: "1100px", margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" },
  ctaTitle: { color: "white", fontSize: "1.1rem", fontWeight: "bold", margin: "0 0 4px" },
  ctaText: { color: "var(--text-muted)", fontSize: "13px", margin: 0 },
  ctaBtn: { background: "var(--accent)", color: "white", border: "none", padding: "12px 24px", borderRadius: "10px", cursor: "pointer", fontSize: "14px", fontWeight: "bold", whiteSpace: "nowrap" },
  homeServiceBadge: { position: "absolute", bottom: "10px", left: "10px", background: "rgba(76,175,80,0.85)", color: "white", padding: "3px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: "bold", backdropFilter: "blur(4px)" },
};